import Image from 'next/image';

import { IconBackground } from '@/atoms';

import type { FunctionComponent } from 'react';
import type { TypeFeatureProps } from './types';

export const Feature: FunctionComponent<TypeFeatureProps> = ({
  className = '',
  Icon,
  image,
  title,
  children,
  variant = 'default',
}: TypeFeatureProps) =>
  variant === 'card' ? (
    // Preset `card` (Figma HoM): tarjeta blanca con sombra, icono en
    // círculo con tinte de acción (secondary @10%) arriba y texto centrado.
    // El círculo envuelve tanto el `Icon` (SVG duotone) como una `image`
    // subida en Strapi, para que ambos rindan igual que el Figma.
    // Tamaños/colores cableados al Figma de las cards (no salen de
    // ThemeConfiguration): título 24/32, cuerpo 16/24, texto neutrals-900.
    <div
      className={`bg-neutrals-0 shadow-light-s flex h-full flex-col items-center gap-4 rounded-[24px] p-8 text-center ${className}`}
    >
      {(image?.src || Icon) && (
        <div className="bg-secondary-500/10 flex h-16 w-16 shrink-0 items-center justify-center rounded-full">
          {image?.src ? (
            <Image
              src={image.src}
              width={40}
              height={40}
              className="h-10 w-10 object-contain"
              alt={title ?? ''}
            />
          ) : (
            Icon && <Icon className="h-10 w-10" />
          )}
        </div>
      )}
      {(title || children) && (
        <div className="flex flex-col items-center gap-2">
          {title && (
            <span className="text-text-neutrals-900 text-[24px] leading-[32px] font-bold">
              {title}
            </span>
          )}

          {children && (
            <span className="text-text-neutrals-900 text-[16px] leading-[24px] font-normal">
              {children}
            </span>
          )}
        </div>
      )}
    </div>
  ) : (
    <div
      className={`flex gap-4 lg:flex-col lg:items-center lg:gap-6 ${className}`}
    >
      {/* If image is true, then render image, else render Icon */}
      {image?.src ? (
        <Image
          src={image?.src ?? ''}
          width={16}
          height={16}
          className="rounded-custom flex h-10 w-10 shrink-0 items-center justify-center md:h-16 md:w-16"
          alt={title ?? ''}
        />
      ) : (
        Icon && <IconBackground Icon={Icon} className="shrink-0" />
      )}
      {(title || children) && (
        <div className="text-body flex flex-col gap-1 lg:items-center lg:gap-2">
          {title && (
            <span className="text-body-l font-medium lg:text-center">
              {title}
            </span>
          )}

          {children && (
            <span className="text-body-m text-text-neutrals-500 font-medium lg:text-center">
              {children}
            </span>
          )}
        </div>
      )}
    </div>
  );

Feature.displayName = 'Feature';
